import React, { createContext, useContext, useReducer, useCallback, useEffect } from "react";
import { getSettings, getCategories } from "@/lib/api";

const StoreContext = createContext(null);

// ─── Translations ───
const translations = {
  RU: {
    home: "ГЛАВНАЯ",
    catalog: "КАТАЛОГ",
    all: "ВСЕ",
    currency: "ВАЛЮТА",
    language: "ЯЗЫК",
    cart: "КОРЗИНА",
    cartEmpty: "Корзина пуста",
    checkout: "ОФОРМЛЕНИЕ",
    items: "ШТ.",
    subtotal: "Подытог",
    promoCode: "Промокод",
    promoPlaceholder: "Введите промокод",
    total: "Итого",
    buy: "КУПИТЬ",
    confirmOrder: "ПОДТВЕРДИТЬ ЗАКАЗ",
    backToCart: "НАЗАД К КОРЗИНЕ",
    name: "Имя",
    namePlaceholder: "Ваше имя",
    contact: "Контакт (Telegram / телефон)",
    contactPlaceholder: "@username или +7...",
    commentLabel: "Комментарий",
    commentPlaceholder: "Пожелания к заказу",
    size: "Размер",
    orderSuccess: "Заказ оформлен! Мы свяжемся с вами.",
    orderError: "Ошибка при оформлении заказа",
    fillNameContact: "Заполните имя и контакт",
    promoApplied: "Промокод применен!",
    promoNotFound: "Промокод не найден",
    selectSize: "Выберите размер",
    soldOut: "РАСПРОДАНО",
    addToCart: "ДОБАВИТЬ В КОРЗИНУ",
    addedToCart: "Добавлено в корзину",
    description: "ОПИСАНИЕ",
    condition: "СОСТОЯНИЕ",
    status: "СТАТУС",
    youMayLike: "ВАМ МОЖЕТ ПОНРАВИТЬСЯ",
    viewAll: "СМОТРЕТЬ ВСЕ",
    viewCatalog: "СМОТРЕТЬ КАТАЛОГ",
    heroSubtitle: "Твой проводник в мир актуального стиля. Новинки, pre-order и эксклюзив.",
    eventFeed: "ЛЕНТА СОБЫТИЙ",
    newArrivals: "НОВОЕ ПОСТУПЛЕНИЕ",
    preOrder: "ПОД ЗАКАЗ",
    inStock: "В НАЛИЧИИ",
    menu: "MENU",
    footerTagline: "Твой проводник в мир актуального стиля",
    linkCopied: "Ссылка скопирована",
    noImage: "НЕТ ФОТО",
    rules: "ПРАВИЛА",
    aboutUs: "О НАС",
    favorites: "ИЗБРАННОЕ",
    favoritesEmpty: "Избранное пусто",
    addedToFavorites: "Добавлено в избранное",
    removedFromFavorites: "Удалено из избранного",
    askQuestion: "ЗАДАТЬ ВОПРОС",
    offerDoc: "Публичная оферта",
    returnDoc: "Возврат и обмен",
    deliveryDoc: "Доставка и оплата",
    privacyDoc: "Обработка персональных данных",
    cookieDoc: "Файлы cookie",
    requisites: "Реквизиты",
    forBuyers: "Покупателю",
    companyCol: "Компания",
    shopCol: "Каталог",
    contactsCol: "Контакты",
    workHours: "Режим работы",
    tradeRegisterNotice: "Интернет-магазин зарегистрирован в Торговом реестре Республики Беларусь",
    agreeConsent: "Я согласен на обработку персональных данных",
  },
  EN: {
    home: "HOME",
    catalog: "CATALOG",
    all: "ALL",
    currency: "CURRENCY",
    language: "LANGUAGE",
    cart: "CART",
    cartEmpty: "Cart is empty",
    checkout: "CHECKOUT",
    items: "ITEMS",
    subtotal: "Subtotal",
    promoCode: "Promo code",
    promoPlaceholder: "Enter promo code",
    total: "Total",
    buy: "BUY",
    confirmOrder: "CONFIRM ORDER",
    backToCart: "BACK TO CART",
    name: "Name",
    namePlaceholder: "Your name",
    contact: "Contact (Telegram / phone)",
    contactPlaceholder: "@username or +7...",
    commentLabel: "Comment",
    commentPlaceholder: "Order notes",
    size: "Size",
    orderSuccess: "Order placed! We will contact you.",
    orderError: "Error placing order",
    fillNameContact: "Please fill name and contact",
    promoApplied: "Promo code applied!",
    promoNotFound: "Promo code not found",
    selectSize: "Select size",
    soldOut: "SOLD OUT",
    addToCart: "ADD TO CART",
    addedToCart: "Added to cart",
    description: "DESCRIPTION",
    condition: "CONDITION",
    status: "STATUS",
    youMayLike: "YOU MAY ALSO LIKE",
    viewAll: "VIEW ALL",
    viewCatalog: "VIEW CATALOG",
    heroSubtitle: "Your guide to contemporary style. New arrivals, pre-order and exclusives.",
    eventFeed: "EVENT FEED",
    newArrivals: "NEW ARRIVALS",
    preOrder: "PRE-ORDER",
    inStock: "IN STOCK",
    menu: "MENU",
    footerTagline: "Your guide to contemporary style",
    linkCopied: "Link copied",
    noImage: "NO IMAGE",
    rules: "RULES",
    aboutUs: "ABOUT US",
    favorites: "FAVORITES",
    favoritesEmpty: "Favorites empty",
    addedToFavorites: "Added to favorites",
    removedFromFavorites: "Removed from favorites",
    askQuestion: "ASK A QUESTION",
    offerDoc: "Public Offer",
    returnDoc: "Returns & Exchange",
    deliveryDoc: "Delivery & Payment",
    privacyDoc: "Personal Data Policy",
    cookieDoc: "Cookies",
    requisites: "Company details",
    forBuyers: "For buyers",
    companyCol: "Company",
    shopCol: "Catalog",
    contactsCol: "Contacts",
    workHours: "Working hours",
    tradeRegisterNotice: "The online store is registered in the Trade Register of the Republic of Belarus",
    agreeConsent: "I consent to the processing of my personal data",
  },
};

const CURRENCY_SYMBOLS = {
  RUB: "RUB",
  USD: "$",
  EUR: "€",
  BYN: "BYN",
};

const initialState = {
  cart: JSON.parse(localStorage.getItem("sizzhka_cart") || "[]"),
  cartOpen: false,
  favorites: JSON.parse(localStorage.getItem("sizzhka_favorites") || "[]"),
  favoritesOpen: false,
  currency: localStorage.getItem("sizzhka_currency") || "RUB",
  language: localStorage.getItem("sizzhka_lang") || "RU",
  exchangeRate: 95,
  exchangeRates: { USD: 95, EUR: 103, BYN: 27 },
  categories: [],
  storeName: "Amberglow",
  storeDescriptionRu: "Одежда с характером. Новинки, винтаж и эксклюзив с доставкой по Беларуси и СНГ.",
  storeDescriptionEn: "Clothing with character. New arrivals, vintage and exclusives with delivery across Belarus and CIS.",
  telegramContact: "XruuXruu",
  legal: {},
};

function reducer(state, action) {
  switch (action.type) {
    case "ADD_TO_CART": {
      const { product, size, quantity = 1 } = action.payload;
      const existIdx = state.cart.findIndex(
        (i) => i.product_id === product.id && i.size === size
      );
      let newCart;
      if (existIdx >= 0) {
        newCart = state.cart.map((item, idx) =>
          idx === existIdx ? { ...item, quantity: item.quantity + quantity } : item
        );
      } else {
        newCart = [...state.cart, {
          product_id: product.id, title: product.title,
          price: product.price, image: product.images?.[0] || "",
          size, quantity,
        }];
      }
      localStorage.setItem("sizzhka_cart", JSON.stringify(newCart));
      return { ...state, cart: newCart };
    }
    case "REMOVE_FROM_CART": {
      const newCart = state.cart.filter((_, idx) => idx !== action.payload);
      localStorage.setItem("sizzhka_cart", JSON.stringify(newCart));
      return { ...state, cart: newCart };
    }
    case "UPDATE_QUANTITY": {
      const { index, quantity } = action.payload;
      if (quantity <= 0) {
        const newCart = state.cart.filter((_, idx) => idx !== index);
        localStorage.setItem("sizzhka_cart", JSON.stringify(newCart));
        return { ...state, cart: newCart };
      }
      const newCart = state.cart.map((item, idx) =>
        idx === index ? { ...item, quantity } : item
      );
      localStorage.setItem("sizzhka_cart", JSON.stringify(newCart));
      return { ...state, cart: newCart };
    }
    case "CLEAR_CART": {
      localStorage.removeItem("sizzhka_cart");
      return { ...state, cart: [] };
    }
    case "TOGGLE_FAVORITE": {
      const product = action.payload;
      const exists = state.favorites.find((f) => f.id === product.id);
      let newFavs;
      if (exists) {
        newFavs = state.favorites.filter((f) => f.id !== product.id);
      } else {
        newFavs = [...state.favorites, {
          id: product.id,
          title: product.title,
          price: product.price,
          old_price: product.old_price,
          images: product.images,
          status: product.status,
          condition: product.condition,
          category: product.category,
        }];
      }
      localStorage.setItem("sizzhka_favorites", JSON.stringify(newFavs));
      return { ...state, favorites: newFavs };
    }
    case "REMOVE_FAVORITE": {
      const newFavs = state.favorites.filter((f) => f.id !== action.payload);
      localStorage.setItem("sizzhka_favorites", JSON.stringify(newFavs));
      return { ...state, favorites: newFavs };
    }
    case "SET_FAVORITES_OPEN": {
      return { ...state, favoritesOpen: action.payload };
    }
    case "SET_CURRENCY": {
      localStorage.setItem("sizzhka_currency", action.payload);
      return { ...state, currency: action.payload };
    }
    case "SET_LANGUAGE": {
      localStorage.setItem("sizzhka_lang", action.payload);
      return { ...state, language: action.payload };
    }
    case "SET_EXCHANGE_RATES": {
      return { ...state, exchangeRates: action.payload };
    }
    case "SET_CATEGORIES": {
      return { ...state, categories: action.payload };
    }
    case "SET_STORE_INFO": {
      const newName = action.payload.store_name || state.storeName;
      if (typeof document !== "undefined") document.title = newName;
      return {
        ...state,
        storeName: newName,
        storeDescriptionRu: action.payload.store_description_ru || state.storeDescriptionRu,
        storeDescriptionEn: action.payload.store_description_en || state.storeDescriptionEn,
        telegramContact: action.payload.telegram_contact || state.telegramContact,
        legal: action.payload || state.legal,
      };
    }
    case "SET_CART_OPEN": {
      return { ...state, cartOpen: action.payload };
    }
    default:
      return state;
  }
}

export function StoreProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Load settings and categories on mount
  useEffect(() => {
    getSettings().then((s) => {
      if (s.exchange_rates) {
        dispatch({ type: "SET_EXCHANGE_RATES", payload: s.exchange_rates });
      }
      dispatch({ type: "SET_STORE_INFO", payload: s });
    }).catch(console.error);

    getCategories().then((d) => {
      if (d.categories) {
        dispatch({ type: "SET_CATEGORIES", payload: d.categories });
      }
    }).catch(console.error);
  }, []);

  const addToCart = useCallback((product, size, quantity) => {
    dispatch({ type: "ADD_TO_CART", payload: { product, size, quantity } });
  }, []);

  const removeFromCart = useCallback((index) => {
    dispatch({ type: "REMOVE_FROM_CART", payload: index });
  }, []);

  const updateQuantity = useCallback((index, quantity) => {
    dispatch({ type: "UPDATE_QUANTITY", payload: { index, quantity } });
  }, []);

  const clearCart = useCallback(() => {
    dispatch({ type: "CLEAR_CART" });
  }, []);

  const toggleFavorite = useCallback((product) => {
    dispatch({ type: "TOGGLE_FAVORITE", payload: product });
  }, []);

  const removeFavorite = useCallback((productId) => {
    dispatch({ type: "REMOVE_FAVORITE", payload: productId });
  }, []);

  const isFavorite = useCallback((productId) => {
    return state.favorites.some((f) => f.id === productId);
  }, [state.favorites]);

  const setFavoritesOpen = useCallback((open) => {
    dispatch({ type: "SET_FAVORITES_OPEN", payload: open });
  }, []);

  const setCurrency = useCallback((currency) => {
    dispatch({ type: "SET_CURRENCY", payload: currency });
  }, []);

  const setLanguage = useCallback((lang) => {
    dispatch({ type: "SET_LANGUAGE", payload: lang });
  }, []);

  const t = useCallback((key) => {
    return translations[state.language]?.[key] || translations.RU[key] || key;
  }, [state.language]);

  const getCategoryLabel = useCallback((slug) => {
    const cat = state.categories.find((c) => c.slug === slug);
    if (!cat) return slug;
    return state.language === "EN" ? cat.label_en : cat.label_ru;
  }, [state.categories, state.language]);

  const storeDescription = state.language === "EN" ? state.storeDescriptionEn : state.storeDescriptionRu;

  const formatPrice = useCallback((priceRub) => {
    if (state.currency === "RUB") {
      return `${priceRub.toLocaleString("ru-RU")} RUB`;
    }
    const rate = state.exchangeRates[state.currency] || 95;
    const converted = Math.round(priceRub / rate);
    const symbol = CURRENCY_SYMBOLS[state.currency] || state.currency;
    if (["USD", "EUR"].includes(state.currency)) {
      return `${symbol}${converted.toLocaleString("en-US")}`;
    }
    return `${converted.toLocaleString("en-US")} ${symbol}`;
  }, [state.currency, state.exchangeRates]);

  const setCartOpen = useCallback((open) => {
    dispatch({ type: "SET_CART_OPEN", payload: open });
  }, []);

  const cartTotal = state.cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const cartCount = state.cart.reduce((sum, i) => sum + i.quantity, 0);
  const favoritesCount = state.favorites.length;

  return (
    <StoreContext.Provider value={{
      ...state, cartTotal, cartCount, favoritesCount, addToCart, removeFromCart,
      updateQuantity, clearCart, setCurrency, setLanguage, formatPrice,
      toggleFavorite, removeFavorite, isFavorite, setFavoritesOpen,
      t, getCategoryLabel, storeDescription, dispatch, CURRENCY_SYMBOLS, setCartOpen,
    }}>
      {children}
    </StoreContext.Provider>
  );
}

export const useStore = () => {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error("useStore must be used within StoreProvider");
  return ctx;
};
